import { Injectable, OnDestroy } from '@angular/core';
import { JobTimeClockLogModel, JobTimeClockModel } from '@app/@models/jobTimeClock.model';
import { differenceInSeconds } from 'date-fns';
import { BehaviorSubject, Observable, Subscription, timer, tap, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TimerService implements OnDestroy {
  private workTimerSubscription: Subscription;
  private breakTimerSubscription: Subscription;
  private workTimeSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private breakTimeSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  private runningClockSubscription: Subscription;
  private runningClockSubject: BehaviorSubject<JobTimeClockModel> = new BehaviorSubject<JobTimeClockModel>(null);
  public runningClock$ = this.runningClockSubject.asObservable();
  private shiftInfo: JobTimeClockLogModel[] = [];

  constructor() {
    this.runningClockSubscription = this.runningClock$.subscribe((clockModel) => {
      if (clockModel) {
        const initialTimer = this.calculateTime(clockModel.tsClockIn, clockModel.tsClockOut);
        this.startWorkTimer(initialTimer);

        try {
          let breakTimer = 0;
          let breakOver = true;
          if (this.shiftInfo?.length) {
            for (let shift of this.shiftInfo) {
              if (shift.isBreak) {
                breakTimer += this.calculateTime(shift.startClock, shift.stopClock);
                breakOver = !!shift.stopClock;
              }
            }
            if (breakTimer > 0) {
              this.startBreakTimer(breakTimer);
              if (breakOver) {
                this.breakTimeSubject.next(breakTimer);
                this.stopBreakTimer(false);
              }
            }
          }
        } catch (err) {}
      } else {
      }
    });
  }

  calculateTime(timeStamp: string, endTimeStamp: string = null): number {
    const startTime = new Date(timeStamp);
    const currentTime = endTimeStamp ? new Date(endTimeStamp) : new Date();
    return differenceInSeconds(currentTime, startTime);
  }

  setRunningClock(obj: JobTimeClockModel, shiftInfo: JobTimeClockLogModel[]): void {
    this.shiftInfo = shiftInfo;
    this.runningClockSubject.next(obj);
  }

  startWorkTimer(initialTime: number = 0): void {
    if (this.workTimerSubscription) {
      this.workTimerSubscription.unsubscribe();
    }
    this.workTimerSubscription = this.createTimer(this.workTimeSubject);
    this.workTimeSubject.next(initialTime);
  }

  stopWorkTimer(): void {
    if (this.workTimerSubscription) {
      this.workTimerSubscription.unsubscribe();
      this.workTimeSubject.next(0); // Reset the timer
    }
  }

  startBreakTimer(initialTime: number = 0): void {
    if (this.breakTimerSubscription) {
      this.breakTimerSubscription.unsubscribe();
    }
    this.breakTimerSubscription = this.createTimer(this.breakTimeSubject);
    this.breakTimeSubject.next(initialTime);
  }

  stopBreakTimer(reset: boolean = false): void {
    if (this.breakTimerSubscription) {
      this.breakTimerSubscription.unsubscribe();
      if (reset) {
        this.breakTimeSubject.next(0); // Reset the timer
      }
    }
  }

  getWorkTimerObservable(): Observable<number> {
    return this.workTimeSubject.asObservable();
  }

  getBreakTimerObservable(): Observable<number> {
    return this.breakTimeSubject.asObservable();
  }

  private createTimer(timeSubject: BehaviorSubject<number>): Subscription {
    return timer(0, 1000)
      .pipe(
        tap(() => {
          const currentValue = timeSubject.value;
          timeSubject.next(currentValue + 1);
        })
      )
      .subscribe();
  }

  stopAllTimers() {
    this.stopWorkTimer();
    this.stopBreakTimer(true);
  }

  ngOnDestroy(): void {
    this.stopAllTimers();

    if (this.runningClockSubscription) {
      this.runningClockSubscription.unsubscribe();
    }
  }

  formatTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secondsLeft = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secondsLeft
      .toString()
      .padStart(2, '0')}`;
  }
}
